import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Target, ShieldCheck, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/site";
import LegalDisclosure from "@/components/LegalDisclosure";
import { FADE_INITIAL, FADE_IN_VIEW, VIEWPORT_ONCE } from "@/lib/motion";

const VALUES = [
  {
    icon: Target,
    title: "Precision & Clarity",
    body: "A mandate of precision, clarity, and professionalism in every document we prepare.",
  },
  {
    icon: GraduationCap,
    title: "MBA & Analytical Rigor",
    body: "Experience in research, analytical writing, and administrative structuring informs every project.",
  },
  {
    icon: ShieldCheck,
    title: "Discretion & Respect",
    body: "Committed to professionalism, discretion, and respect in every client interaction.",
  },
];

export default function About() {
  return (
    <div className="bg-white">
      <section className="bg-[#0B1B3D]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="section-label text-[#D4AF37]">About Draft &amp; Defend</span>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold tracking-tight text-white sm:text-5xl">
            A professional scrivener service built on precision and trust.
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <motion.div
            initial={FADE_INITIAL}
            whileInView={FADE_IN_VIEW}
            viewport={VIEWPORT_ONCE}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Sparkles className="h-5 w-5" />
              <span className="section-label">Our Story</span>
            </div>
            <div className="mt-5 space-y-5 text-[15px] leading-relaxed text-slate-600">
              <p>
                Draft &amp; Defend was founded by a business professional with an MBA and hands-on
                experience in research, analytical writing, and administrative structuring. What
                began as a commitment to clear, well-organized documents grew into a dedicated
                document preparation service for self-represented individuals.
              </p>
              <p>
                Our mandate is simple: precision, clarity, and professionalism. We help
                self-represented individuals present court-ready, well-organized documents by
                typing, formatting, and structuring exactly what our clients direct — never
                interpreting, choosing, or advising on legal strategy.
              </p>
              <p>
                We are committed to professionalism, discretion, and respect in every client
                interaction. Whether you are responding to an eviction petition, a debt claim, or
                preparing a resume that clears applicant tracking systems, our goal is the same: a
                clean, professional document you can proudly present.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={VIEWPORT_ONCE}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-lg border border-slate-200 shadow-md">
              <img src={IMAGES.aboutDesk} alt="Documents, pen, and notebook on a desk" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-lg bg-[#0B1B3D] px-7 py-5 text-white shadow-lg sm:block">
              <p className="font-serif text-3xl font-bold text-[#D4AF37]">MBA</p>
              <p className="text-xs tracking-wide text-slate-300">Level Document Preparation</p>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid gap-8 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={FADE_INITIAL}
              whileInView={FADE_IN_VIEW}
              viewport={VIEWPORT_ONCE}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-8"
            >
              <v.icon className="h-8 w-8 text-[#D4AF37]" />
              <h3 className="mt-4 font-serif text-xl font-semibold text-[#0B1B3D]">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-24 sm:px-6 lg:px-8">
        <LegalDisclosure />
      </section>
    </div>
  );
}
